import type { ServiceState } from "@/lib/types";
import { cx } from "@/lib/format";

/**
 * The lamp. Signature element, and the only saturated thing in the system.
 *
 * # WHAT IT IS FOR
 *
 * A control plane is a thing you scan. The question is almost never "what is
 * this one service doing" — it is "is anything wrong", asked of a list of
 * forty. So the answer has to be readable down a column, at a glance, without
 * reading any words.
 *
 * That is a patch card. One lamp per row, all on the same vertical line, in
 * a gutter of fixed width so the line survives a row that has nothing to
 * report. An unlit lamp is drawn, not omitted: a gap in the column reads as a
 * missing row rather than as a quiet one.
 *
 * # WHY IT IS A LENS AND NOT A CIRCLE
 *
 * Six pixels of flat colour is a dot. Six pixels with a lighter centre and a
 * slightly darker rim is a lens with something behind it — which is what the
 * thing on the front of a switch actually looks like, and it survives being
 * small in a way a flat disc does not.
 *
 * # LIVE STATES
 *
 * The lamp holds still and a ring breathes around it. Motion in the dot
 * itself, at this size, is indistinguishable from a rendering fault — which
 * is not a guess: the fading dot this replaces was read as a broken image by
 * more than one person. A ring has room to move.
 */
export type LampState = "up" | "warn" | "down" | "busy" | "idle";

const LIT: Record<LampState, string> = {
  up: "var(--success)",
  warn: "var(--warning)",
  down: "var(--danger)",
  busy: "var(--primary)",
  idle: "var(--text-faint)",
};

export function Lamp({
  state,
  live,
  className,
  title,
}: {
  state?: LampState;
  /** Shorthand for the commonest question: is this one still going on? */
  live?: boolean;
  className?: string;
  /** Only when the lamp stands alone. Beside a word, the word is the label. */
  title?: string;
}) {
  const resolved: LampState = state ?? (live ? "busy" : "idle");
  const colour = LIT[resolved];
  const breathing = resolved === "busy";

  return (
    <span
      className={cx("relative inline-flex h-2 w-2 shrink-0", className)}
      title={title}
      aria-hidden={title ? undefined : true}
      role={title ? "img" : undefined}
      aria-label={title}
    >
      {/* The ring, for live states. Outside the lamp so the lamp can hold
          still, and at 1px so a breathing row does not pull the eye harder
          than a failing one. */}
      {breathing && (
        <span
          className="animate-pulse-soft absolute -inset-1 rounded-full border"
          style={{ borderColor: colour }}
        />
      )}
      <span
        className="h-2 w-2 rounded-full"
        style={{
          background: colour,
          // A soft ring in the dot's own colour, so a 8px dot reads as a
          // status light rather than as a full stop.
          boxShadow: `0 0 0 3px color-mix(in srgb, ${colour} 16%, transparent)`,
        }}
      />
    </span>
  );
}

/**
 * The gutter a lamp lives in. 20px, fixed.
 *
 * Exported as a component rather than left to each caller's class list,
 * because the whole value of the thing is that it is the same 20px in the
 * services table, the databases table, the fleet, the ticket list and the
 * invoice list. Six hand-written grids drift within a week.
 */
export function Gutter({ children }: { children?: React.ReactNode }) {
  return <span className="flex w-5 shrink-0 items-center justify-center">{children}</span>;
}

/* --------------------------------------------------------------------------
 * The product's state machine, as lamps and words.
 *
 * The map is exhaustive over ServiceState on purpose: a state added to the
 * API and not considered here is a compile error, not a blank space in a
 * table. That property is inherited from the system this replaces and is
 * worth more than anything about how it looks.
 *
 * Labels are the customer's vocabulary, never Kubernetes' (PD-3).
 * -------------------------------------------------------------------------- */

const SERVICE: Record<ServiceState, { label: string; lamp: LampState }> = {
  QUEUED: { label: "Queued", lamp: "busy" },
  BUILDING: { label: "Building", lamp: "busy" },
  DEPLOYING: { label: "Deploying", lamp: "busy" },
  STARTING: { label: "Starting", lamp: "busy" },
  RUNNING: { label: "Running", lamp: "up" },
  DEGRADED: { label: "Degraded", lamp: "warn" },
  FAILED: { label: "Failed", lamp: "down" },
  STOPPED: { label: "Stopped", lamp: "idle" },
  SUSPENDED: { label: "Suspended", lamp: "warn" },
  DELETING: { label: "Deleting", lamp: "busy" },
};

export function serviceLamp(state: ServiceState): LampState {
  return SERVICE[state]?.lamp ?? "idle";
}

export function serviceLabel(state: string): string {
  return SERVICE[state as ServiceState]?.label ?? state;
}

/**
 * A lamp and the word for it, for use outside a table.
 *
 * The word is ordinary text at ordinary weight. It is not a tinted capsule,
 * because ninety-eight tinted capsules is a wall of colour in which no single
 * row stands out — and a row standing out is the only reason to spend colour
 * on state at all.
 */
export function StateLine({
  state,
  className,
}: {
  state: ServiceState;
  className?: string;
}) {
  const it = SERVICE[state];
  return (
    <span className={cx("inline-flex items-center gap-2", className)}>
      <Lamp state={it?.lamp ?? "idle"} />
      <span className="t-body">{it?.label ?? state}</span>
    </span>
  );
}
